import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  QuoteProductCustomization,
  QuoteProductDimension,
} from '../../../quotes/otherQuoteEntities';
import QuoteProductDetails from '../../../quotes/review/quote-products/QuoteProductDetails';
import QuoteProductLeadTime from '../../../quotes/lead-times/QuoteProductLeadTime';
import { ProductConfiguration } from '../../../products/productConfiguration';
import {
  getMsrpColumnClassNames,
  getDNetColumnClassNames,
} from '../../../quotes/quotePriceMode';
import InventoryItemBadge from '../../../quotes/InventoryItemBadge';
import LeadTimeDetailsModal from '../../review/quote-products/LeadTimeDetailsModal';
import { Alert } from 'reactstrap';
import { ProductGroupTypes } from '../../../products/productGroup';
import MachineBoltPatternType from '../../../machines/machineBoltPatternType';
import ReviewQuoteProductPricing from '../../review/quote-products/ReviewQuoteProductPricing';
import { appConfig } from '@ami/react/core';
import QuoteView from '../../../quotes/view/quoteView';
import { QuoteProductDto } from '../../../quotes/otherQuoteEntities';
import { QuoteProductConfiguration } from '../../../quotes/quoteProductConfiguration';

export default class QuoteProductComponent extends React.Component {
  constructor(props) {
    super(props);
    this.leadTimeDetailsModalRef = React.createRef();
    this.toggle = this.toggle.bind(this);
    this.state = {
      tooltipOpen: false,
    };
  }

  static propTypes = {
    product: PropTypes.object.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration).isRequired,
    selectedPriceMode: PropTypes.string.isRequired,
    isoCurrencySymbol: PropTypes.string.isRequired,
    quote: PropTypes.instanceOf(QuoteView).isRequired,
    index: PropTypes.number.isRequired,
    onLeadTimeOverrideUpdated: PropTypes.func.isRequired,
    refreshQuote: PropTypes.func.isRequired
  };

  toggle() {
    this.setState({
      tooltipOpen: !this.state.tooltipOpen,
    });
  }

  updateLeadTimeOverride = (leadTime) => {
    const { index } = this.props;
    this.props.onLeadTimeOverrideUpdated(leadTime, index);
    this.forceUpdate();
  };

  render() {
    const { product, productConfiguration, quoteProductConfiguration, selectedPriceMode, isoCurrencySymbol, quote, refreshQuote } = this.props;

    const msrpClassNames = getMsrpColumnClassNames(selectedPriceMode);
    const dNetClassNames = getDNetColumnClassNames(selectedPriceMode);

    const highlightRowClass = quote.maskPricingAndLeadTimes && !!product.manufacturingInstructions && product.requiresReview  && !product.isReviewed;
    const maskPricingAndLeadTimes = highlightRowClass && !appConfig.isManagementApp;

    const alertInterferesWithFactoryMount =
      (product.productGroupType === ProductGroupTypes.ProLinkThumb.id ||
        product.productGroupType === ProductGroupTypes.WeldOnThumb.id) &&
      (quote.attachmentPoints.some(
        (x) => x.machineOemThumbMount === 'HasThumbMount'
      ) ||
        quote.attachmentPoints.some(
          (x) =>
            x.boltPattern === MachineBoltPatternType.HasBoltPattern &&
            x.boltOnMount === 'HasOemBoltOnMount'
        ));

    const leadTimeOverrideMode = quote.statusInformation.canBeEdited;

    return (
      <Fragment>
        <tr className={highlightRowClass ? 'table-warning' : ''}>
          <QuoteProductDetails
            productGroupImageUrl={product.productGroupImageUrl}
            productGroupName={product.productGroupName}
            productGroupDescription={product.productGroupDescription}
            productPartNumber={product.productPartNumber}
            productPartNumberIncomplete={product.productPartNumberIncomplete}
            productOptions={product.productOptions}
            standaloneComponents={product.standaloneComponents}
            customizations={product.customizations.map(
              (x) => new QuoteProductCustomization(x)
            )}
            dimensions={product.dimensions.map(
              (x) => new QuoteProductDimension(x)
            )}
            marketing={product.marketing}
            productConfiguration={productConfiguration}
            notes={product.notes ? product.notes.content : null}
            manufacturingInstructions={product.manufacturingInstructions}
            paintColor={product.paintColor || product.inventoryItemPaintColor}
            notesFieldName={product.notes ? product.notes.fieldName : null}
            quote={quote}
            productDetail={product.detail}
            maskPricingAndLeadTimes={maskPricingAndLeadTimes}
          />
          <td data-title="Lead Time" className="lead-time">
            <QuoteProductLeadTime
              leadTime={product.leadTime}
              quoteProductTotalLeadTimeInDays={product.totalLeadTimeInDays}
              onLeadTimeViewDetailsClick={() => {
                this.leadTimeDetailsModalRef.current.show();
              }}
              maskPricingAndLeadTimes={maskPricingAndLeadTimes}
            />
            {product.isInventory && (
              <InventoryItemBadge inStock={product.inventoryInStock} isConsignment={product.isConsignment} />
            )}
          </td>
          <td className="price-breakdown">
            {product.pricing && <ReviewQuoteProductPricing
              pricing={product.pricing}
              selectedPriceMode={selectedPriceMode}
              isoCurrencySymbol={isoCurrencySymbol}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
              quoteProduct={product}
              maskPricingAndLeadTimes={maskPricingAndLeadTimes}
            />}
          </td>
        </tr>
        {alertInterferesWithFactoryMount && (
          <tr>
            <td style={{ borderTop: '0px' }}></td>
            <td colSpan="6" style={{ borderTop: '0px' }}>
              <div className="row text-start">
                <Alert color="danger">
                  <i className="fa fa-exclamation-circle fa-lg text-danger" />{' '}
                  Product requires the machine's OEM thumb mount to be removed
                  (if present)
                </Alert>
              </div>
            </td>
          </tr>
        )}
        <LeadTimeDetailsModal
          ref={this.leadTimeDetailsModalRef}
          quoteProduct={new QuoteProductDto(product)}
          quoteId={quote.id}
          quoteProductConfiguration={quoteProductConfiguration}
          isOverrideMode={leadTimeOverrideMode}
          onLeadTimeOverrideUpdated={this.updateLeadTimeOverride}
          refreshQuote={refreshQuote}
        />
      </Fragment>
    );
  }
}
